import { type ThreatVector } from './context/threat-vector/entity';
import { MatrixValueGroup } from './matrixes';
import { type TraV1 } from './tra-v1';

export function resolveRisk(
    data: TraV1,
    threatVector: ThreatVector,
): string | undefined {
    const { targetAssetId, protectionGoalId, exploitabilityId } = threatVector;

    if (!targetAssetId || !protectionGoalId || !exploitabilityId) return;

    const targetAsset = data.assets.getById(targetAssetId);
    const exposureId = data.zones.getById(
        targetAsset?.zoneId ?? '',
    )?.exposureId;
    if (!exposureId) return;

    const likelihoodId = data.likelihoodMatrix.getValue(
        exposureId,
        exploitabilityId,
    );
    if (!likelihoodId) return;

    const impact =
        data.assetProtectionGoalImpacts.getByAssetIdAndProtectionGoalId(
            targetAssetId,
            protectionGoalId,
        );
    if (!impact?.impactId) return;

    return data.riskMatrix.getValue(likelihoodId, impact.impactId) ?? undefined;
}

export interface RiskLevelStat {
    id: string;
    name: string;
    color: string;
    count: number;
}

export interface TraStatistics {
    vectors: number;
    assetsInScope: number;
    mitigations: number;
    tasks: number;
    risks: Record<string, number>;
    residualRisks: Record<string, number>;
    riskLevels: RiskLevelStat[];
}

function resolveResidualRisk(
    data: TraV1,
    threatVector: ThreatVector,
): string | undefined {
    const { targetAssetId, protectionGoalId, exploitabilityId } = threatVector;
    if (!targetAssetId || !protectionGoalId || !exploitabilityId) return;

    const mitigations =
        data.threatVectorMitigations.getByThreatVectorId(threatVector.id) ?? [];
    const last = mitigations[mitigations.length - 1];

    const targetAsset = data.assets.getById(targetAssetId);
    const exposureId =
        last?.exposureId ??
        data.zones.getById(targetAsset?.zoneId ?? '')?.exposureId;
    if (!exposureId) return;

    const likelihoodId = data.likelihoodMatrix.getValue(
        exposureId,
        last?.exploitabilityId ?? exploitabilityId,
    );
    if (!likelihoodId) return;

    const impact = last?.impactId
        ? { impactId: last.impactId }
        : data.assetProtectionGoalImpacts.getByAssetIdAndProtectionGoalId(
              targetAssetId,
              protectionGoalId,
          );
    if (!impact?.impactId) return;

    return data.riskMatrix.getValue(likelihoodId, impact.impactId) ?? undefined;
}

export function computeTraStatistics(data: TraV1): TraStatistics {
    const threatVectors = data.threatVectors.getAll();

    const risks = threatVectors.reduce<Record<string, number>>((acc, tv) => {
        const riskId = resolveRisk(data, tv);
        if (riskId) acc[riskId] = (acc[riskId] ?? 0) + 1;
        return acc;
    }, {});

    const residualRisks = threatVectors.reduce<Record<string, number>>(
        (acc, tv) => {
            const riskId = resolveResidualRisk(data, tv);
            if (riskId) acc[riskId] = (acc[riskId] ?? 0) + 1;
            return acc;
        },
        {},
    );

    const riskLevels: RiskLevelStat[] = (
        data.matrixValues.getAllByGroup(MatrixValueGroup.Risk) ?? []
    ).map((mv) => ({
        id: mv.id,
        name: mv.name,
        color: mv.color,
        count: risks[mv.id] ?? 0,
    }));

    return {
        vectors: threatVectors.length,
        assetsInScope: data.assets.getInScope().length,
        mitigations: data.threatVectorMitigations.getAll().length,
        tasks: data.threatVectorMitigationTasks.getAll().length,
        risks,
        residualRisks,
        riskLevels,
    };
}
